import { Injectable } from '@angular/core';
import { CoreModule } from '../../core.module';

import { UserService } from './user.service';
import { PromoCodeService } from './promo-code.service';

import { CurrentExpense } from '../../../types/current-expense.interface';
import { IPromocode, IUser } from '../../../types/datatypes';
import { IPopulatedFeatureBookOnly } from '../../../types/populated';
import { PromocodeAPIResponse } from '../../../types/responses';

@Injectable({
	providedIn: CoreModule,
})
export class DiscountService {
	public username: string;

	public _memberPromoCode: IPromocode;

	private _user: IUser;

	constructor(
		private userService: UserService,
		private promoCodeService: PromoCodeService,
	) {
		this.userService.fetchUser().then(this._handleUser.bind(this)).catch(() => {});
	}

	async applyMemberPromoCode(originalAmount: number, feature: IPopulatedFeatureBookOnly): Promise<number> {
		await this._fetchMemberPromoCodes();

		if (!this._memberPromoCode) {
			return 0;
		}

		const { value: discount, currentLimit, _id, discountLimit } = this._memberPromoCode;
		const availableAmount = discountLimit - currentLimit;
		const { discountAmount, discountedPrice } = this._calculateDiscount(originalAmount, discount, availableAmount);

		feature._promoCode = _id;
		feature.paymentAmount = discountedPrice;

		const currentExpense = { currentExpense: discountAmount };

		await this._logPromoCodeUse(_id, currentExpense);

		return discountAmount;
	}

	async getMemberDiscount(
		originalAmount: number,
	): Promise<{ discountAmount: number; discountedPrice: number; discount: number; promoCode: IPromocode}> {
		// We're doubling up here because the constructor is a race condition... This is semi hacky
		// will want to remove.
		if (!this.username) {
			const user = await this.userService.fetchUser();
		    this._handleUser(user as IUser);
		}

		await this._fetchMemberPromoCodes();

		if (!this._memberPromoCode) {
			return {
				discountAmount: 0,
				discountedPrice: originalAmount,
				discount: 0,
				promoCode: null,
			};
		}

		const { value: discount, currentLimit, discountLimit } = this._memberPromoCode;
		const availableAmount = discountLimit - currentLimit;
		const { discountAmount, discountedPrice } = this._calculateDiscount(originalAmount, discount, availableAmount);
		return {
			discountAmount,
			discountedPrice,
			discount,
			promoCode: this._memberPromoCode,
		};
	}

	_handleUser(user: IUser) {
		this.username = user.username;
	}

	async _fetchMemberPromoCodes() {
		if (!this.username) {
			return;
		}

		const promocode: PromocodeAPIResponse = await this._getMemberShipPromoCode();

		if (promocode.success && promocode.promoCode) {
			this._memberPromoCode = promocode.promoCode;
		}
	}

	_getMemberShipPromoCode(): Promise<PromocodeAPIResponse> {
		return new Promise((resolve, reject) => this.promoCodeService.getMembershipPromocode(this.username).subscribe(resolve, reject));
	}

	_logPromoCodeUse(promoCodeId: string, currentExpense: CurrentExpense): Promise<any> {
		return new Promise((resolve, reject) => this.promoCodeService.logUse(promoCodeId, currentExpense).subscribe(resolve, reject));
	}

	_calculateDiscount(originalAmount = 0, discount = 0, availableAmount = 0, discountType = 'percentage') {
		let _discount = discount;
		if (originalAmount === 0 || availableAmount <= 0 || _discount <= 0) {
			return {
				discountAmount: 0,
				discountedPrice: originalAmount,
			};
		}

		let discountAmount;

		if (discountType === 'amount') {
			if (_discount > originalAmount) _discount = originalAmount;
			discountAmount = _discount;
		} else {
			if (_discount > 100) _discount = 100;
			discountAmount = (originalAmount * (_discount / 100));
		}

		if (this._discountAmountExceedsAvailableAmount(discountAmount, availableAmount)) {
			discountAmount = availableAmount;
		}

		const discountedPrice = originalAmount - discountAmount;

		return {
			discountAmount,
			discountedPrice,
		};
	}

	_discountAmountExceedsAvailableAmount(discountAmount: number, availableLimit: number): boolean {
		return discountAmount > availableLimit;
	}
}
